<template>
  <my-giving-layout :enable-scroll-event="true">
    <div class="bg-[#F6F4F0] w-full h-viewport">
      <BackButton
        class="w-full px-[16px] md:px-[24px] lg:px-[28px] mb-[8px] md:mb-[20px] lg:mb-[24px]"
        :customBack="back"
      />
      <div class="px-[16px] md:px-[24px] lg:px-[28px] pb-[48px] md:pb-[56px] lg:pb-[64px]">
        <!-- Header and images, 20px apart as the F frames space them -->
        <div
          class="flex flex-col gap-[20px] md:gap-[24px] lg:gap-[28px] mb-[20px] md:mb-[24px] lg:mb-[28px]"
        >
          <img
            src="assets/images/my-giving/building-fund/TithingComitment_HOGCAPP.png"
            alt="Building Fund"
            class="w-[110px] h-[92px] md:w-[138px] md:h-[115px] lg:w-[165px] lg:h-[138px] object-contain object-left"
          />
          <p
            class="font-black text-brown-800 text-[2rem] md:text-[2.25rem] lg:text-[2.5rem] leading-[2.25rem] md:leading-[2.75rem] lg:leading-[3rem] tracking-[-0.02em]"
          >
            My BF Fulfillment
          </p>
        </div>
        <!-- Building fund year dropdown, 150x52 as the F frames draw it -->
        <GivingDropDown
          :selected="routeYear"
          :options="bfPledgeYears"
          type="bf"
          large
          class="w-[150px] md:w-[160px] lg:w-[180px] min-h-[52px] lg:min-h-[56px] rounded-[12px]"
        />
        <!-- 2026 on: giving-api's own fulfilment. Earlier years keep the tables below. -->
        <BuildingFundFulfilment v-if="showFulfilmentView" ref="fulfilment" :year="routeYear" />
        <template v-else>
          <!-- Progress tracker -->
          <ProgressTracker
            class="mb-[20px] md:mb-[24px] lg:mb-[28px]"
            :total-pledge-amount="totalPledgeAmount"
            :amount-fulfilled="totalAmountFulfilled"
          />
          <!-- Table -->
          <div class="flex flex-col gap-[20px] md:gap-[24px] lg:gap-[28px]">
            <!-- Table 1: Building Fund -->
            <TableBF
              v-if="showTable(bfPledge)"
              :titleCSSPropsStyle="tableClass"
              overallDivClassCustom="mb-5"
              :pledge-data="bfPledge"
              :fulfill-data="bfFulfillmentRecord"
              :selected-year="this.selectedYear"
              fund-type="Building Fund"
              :secondaryPledge="secondaryPledge"
              :couplePledgeData="couplePledgeData"
              :mainPledgerName="mainPledgerName"
            />

            <!-- Table 2: General Fund -->
            <TableBF
              v-if="showTable(gfPledge)"
              :titleCSSPropsStyle="tableClass"
              overallDivClassCustom="mb-5"
              :pledge-data="gfPledge"
              :fulfill-data="gfFulfillmentRecord"
              :selected-year="this.selectedYear"
              fund-type="General Fund"
              :secondaryPledge="secondaryPledge"
              :couplePledgeData="couplePledgeData"
              :mainPledgerName="mainPledgerName"
            />

            <!-- Table 3: Heart Community Services Fund -->
            <TableBF
              v-if="showTable(hcsPledge)"
              :titleCSSPropsStyle="tableClass"
              overallDivClassCustom="mb-5"
              :pledge-data="hcsPledge"
              :fulfill-data="hcsFulfillmentRecord"
              :selected-year="this.selectedYear"
              fund-type="Heart Community Services (HCS)"
              :secondaryPledge="secondaryPledge"
              :couplePledgeData="couplePledgeData"
              :mainPledgerName="mainPledgerName"
            />

            <!-- Table xx: Start tithing -->
            <TableTitheMsg
              v-if="titheStart"
              :titleCSSPropsStyle="tableClass"
              overallDivClassCustom="mb-5"
              :selected-year="this.selectedYear"
              fund-type="Tithing"
              commit-pledge="I commit to start tithing monthly"
            />
            <!-- Table xx: Continue tithing -->
            <TableTitheMsg
              v-if="titheContinue"
              :titleCSSPropsStyle="tableClass"
              overallDivClassCustom="mb-10"
              :selected-year="this.selectedYear"
              fund-type="Tithing"
              commit-pledge="I commit to continue tithing monthly"
            />
          </div>
          <GiveNowButton class="mb-10" />
        </template>
        <AdditionalInfo />
      </div>
    </div>
  </my-giving-layout>
</template>

<script>
import { mapState } from 'vuex';
import BackButton from '@/components/common/BackButton.vue';
import TableBF from '@/components/MyGiving/BuildingFundPage/TableBF.vue';
import { commitmentTotal } from '@/utils/bf-commitment-months';
import TableTitheMsg from '@/components/MyGiving/BuildingFundPage/TableTitheCommitment.vue';
import GivingDropDown from '@/components/MyGiving/BuildingFundPage/GivingDropdown.vue';
import AdditionalInfo from '@/components/MyGiving/Common/AdditionalInfo.vue';
import ProgressTracker from '@/components/MyGiving/BuildingFundPage/ProgressTracker.vue';
import GiveNowButton from '@/components/MyGiving/Common/GiveNowButton.vue';
import RegisterBackHandler from '@/mixins/RegisterBackHandler.js';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
import BuildingFundFulfilment from '@/components/MyGiving/BuildingFundPage/BuildingFundFulfilment.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import {
  BF_PLEDGE_YEAR,
  BUILDING_FUND_PLEDGE_FLAG,
} from '@/constants/MyGiving/building-fund-pledge';

export default {
  mixins: [RegisterBackHandler],
  components: {
    BackButton,
    TableBF,
    TableTitheMsg,
    GivingDropDown,
    AdditionalInfo,
    ProgressTracker,
    GiveNowButton,
    MyGivingLayout,
    BuildingFundFulfilment,
  },
  data() {
    return {
      selectedFund: 'BF',
      tableClass: 'bg-[#554638] text-white',
    };
  },
  computed: {
    ...mapState('myGiving', [
      'bfPledgeYears',
      'bfPledge',
      'gfPledge',
      'hcsPledge',
      'bfFulfillmentRecord',
      'gfFulfillmentRecord',
      'hcsFulfillmentRecord',
      'titheStart',
      'titheContinue',
      'selectedYear',
      'secondaryPledge',
      'couplePledgeData',
      'mainPledgerName',
    ]),

    routeYear() {
      return Number(this.$route.params.id);
    },

    // The BF2026 pledge's flag also gates its fulfilment page. Read inside the
    // computed, as the landing page does, so specs can mock the composable.
    showFulfilmentView() {
      const { isEnabled } = useFeatureFlags();
      return this.routeYear >= BF_PLEDGE_YEAR && isEnabled(BUILDING_FUND_PLEDGE_FLAG);
    },

    totalPledgeAmount() {
      // One-time plus every month (five from BF2026), across the three funds.
      return [this.bfPledge, this.gfPledge, this.hcsPledge].reduce(
        (sum, pledge) => sum + commitmentTotal(pledge),
        0,
      );
    },
    totalAmountFulfilled() {
      const allFulfillments = [
        this.bfFulfillmentRecord,
        this.gfFulfillmentRecord,
        this.hcsFulfillmentRecord,
      ];
      // loop through allFulfillments and sum up the fulfillment amount
      let sum = 0;
      for (const fulfillment of allFulfillments) {
        if (!fulfillment) {
          continue;
        }
        sum += fulfillment?.reduce((sum, element) => sum + Number(element.amount), 0);
      }
      return sum;
    },
  },
  async ionViewWillEnter() {
    this.$store.commit('myGiving/setSelectedYear', this.$route.params.id);

    if (this.showFulfilmentView) {
      // The year list only feeds the dropdown, so both load at once. The component
      // may only just have rendered, on a year switch from an earlier year.
      await this.$nextTick();
      await Promise.all([
        this.$store.dispatch('myGiving/getBFPledgeYears'),
        this.$refs.fulfilment.load(),
      ]);
      return;
    }

    await this.$store.dispatch('myGiving/getBFPledgeYears');

    await this.$store.dispatch(
      'myGiving/getBFPledgeByYears',
      //this.selectedYear
      this.$route.params.id,
    );

    await this.$store.dispatch('myGiving/getBfRecordByYear', this.selectedYear);
  },
  methods: {
    back() {
      this.$router.replace('/my-giving');
    },
    showTable(pledgeData) {
      if (
        pledgeData?.one_time_commitment !== null ||
        pledgeData?.month_1_commitment !== null ||
        pledgeData?.month_2_commitment !== null ||
        pledgeData?.month_3_commitment !== null ||
        pledgeData?.month_4_commitment !== null ||
        pledgeData?.month_5_commitment !== null
      ) {
        return true;
      }
      return false;
    },
  },
};
</script>
